import { useContext, useState, useEffect, useMemo } from "react";
import { Card, OverlayTrigger, Tooltip } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import { ThemeContext } from "../../../../theme/ThemeContext";
import AuthContext from "../../../../auth/AuthContext";
import { Skeleton } from "primereact";
import Moment from "moment";
import DashboardService from "../../../../services/DashboardService";
import { FiArrowRight } from "react-icons/fi";

const CalenderComponent = () => {
  const [date, setDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [eventTypeFilter, setEventTypeFilter] = useState("course");
  const [courseFilter, setCourseFilter] = useState("");

  const { theme } = useContext(ThemeContext);
  const isDark = theme === "dark";
  const authContext = useContext(AuthContext);

  // Calendar range constants
  const CURRENT_YEAR = new Date().getFullYear();
  const MIN_YEAR = 2014;
  const MIN_MONTH = 0; // January
  const MAX_YEAR = CURRENT_YEAR + 5;
  const MAX_MONTH = 11; // December

  const tooltipBg = "#ffffff";
  const tooltipText = "#000000";
  const tooltipBorder = isDark ? "#555" : "#ddd";

  const tooltipStyles = `
    .tooltip-inner {
      background-color: ${tooltipBg} !important;
      color: ${tooltipText} !important;
      border: 1px solid ${tooltipBorder};
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
      font-size: 10px;
    }
    .tooltip.bs-tooltip-top .tooltip-arrow::before {
      border-top-color: ${tooltipBg} !important;
    }
    .tooltip.bs-tooltip-bottom .tooltip-arrow::before {
      border-bottom-color: ${tooltipBg} !important;
    }
    .tooltip.bs-tooltip-left .tooltip-arrow::before {
      border-left-color: ${tooltipBg} !important;
    }
    .tooltip.bs-tooltip-right .tooltip-arrow::before {
      border-right-color: ${tooltipBg} !important;
    }
    .tooltip { opacity: 1 !important; }
  `;

  const monthNames = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
  ];

  const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  const eventColors = {
    quiz: "#4A90E2",
    assessment: "#9B59B6",
  };
  const courseDefaultColor = "#F5A623";

  const courseColorsPalette = useMemo(
    () => [
      "#5fbfe6", "#9B59B6", "rgba(24, 104, 136, 1)", "#F5A623",
      "#43A047", "#4A90E2", "#7b1fa2", "#ff5722",
      "#512da8", "#8bc34a", "#f06292",
    ],
    []
  );

  // Years dropdown
  const years = useMemo(() => {
    const arr = [];
    for (let y = MIN_YEAR; y <= MAX_YEAR; y++) {
      arr.push(y);
    }
    return arr;
  }, [MIN_YEAR, MAX_YEAR]);

  const monthlyCourses = useMemo(() => {
    return [
      ...new Set(
        events
          .filter((ev) => ev.type?.toLowerCase() === "course")
          .filter(
            (ev) =>
              new Date(ev.plannedEndDate) >= new Date(date.getFullYear(), date.getMonth(), 1) &&
              new Date(ev.plannedStartDate) <= new Date(date.getFullYear(), date.getMonth() + 1, 0)
          )
          .map((ev) => ({ id: ev.id, name: ev.name }))
      ),
    ].sort((a, b) => a.name.localeCompare(b.name));
  }, [events, date]);

  const courseColorMap = useMemo(() => {
    if (monthlyCourses.length === 0) return {};
    const map = {};
    monthlyCourses.forEach((c, idx) => {
      map[c.id] = courseColorsPalette[idx % courseColorsPalette.length];
    });
    return map;
  }, [monthlyCourses, courseColorsPalette]);

  const getCourseColor = (courseId) =>
    courseColorMap[courseId] || courseDefaultColor;

  // Disable arrows when at boundaries
  const isPrevDisabled = useMemo(() => {
    return date.getFullYear() === MIN_YEAR && date.getMonth() === MIN_MONTH;
  }, [date, MIN_YEAR, MIN_MONTH]);

  const isNextDisabled = useMemo(() => {
    return date.getFullYear() === MAX_YEAR && date.getMonth() === MAX_MONTH;
  }, [date, MAX_YEAR, MAX_MONTH]);

  const handleYearChange = (e) => {
    const selectedYear = Number(e.target.value);
    setDate((prev) => {
      let month = prev.getMonth();
      if (selectedYear === MIN_YEAR && month < MIN_MONTH) {
        month = MIN_MONTH;
      }
      if (selectedYear === MAX_YEAR && month > MAX_MONTH) {
        month = MAX_MONTH;
      }
      return new Date(selectedYear, month, 1);
    });
  };

  const changeMonth = (offset) => {
    setDate((prev) => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() + offset);

      const year = newDate.getFullYear();
      const month = newDate.getMonth();

      if (year < MIN_YEAR || (year === MIN_YEAR && month < MIN_MONTH)) {
        return prev;
      }
      if (year > MAX_YEAR || (year === MAX_YEAR && month > MAX_MONTH)) {
        return prev;
      }

      return newDate;
    });
  };

  const getDaysInMonth = (year, month) => {
    const firstDay = new Date(year, month, 1).getDay();
    const totalDays = new Date(year, month + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < firstDay; i++) cells.push(null);
    for (let i = 1; i <= totalDays; i++) cells.push(i);
    return cells;
  };

useEffect(() => {
    const controller = new AbortController(); 

    const fetchCalendarData = async () => {
      setLoading(true);
      try {
        const startDate = Moment(
          new Date(date.getFullYear(), date.getMonth(), 1)
        ).format("YYYY-MM-DD");
        const endDate = Moment(
          new Date(date.getFullYear(), date.getMonth() + 1, 0)
        ).format("YYYY-MM-DD");

        const response = await DashboardService.GetCalenderChart(
          authContext,
          startDate,
          endDate,
          controller.signal 
        );

        if (controller.signal.aborted) return; 

        if (!response) return;
        if (response && response.isSuccess && Array.isArray(response.data)) {
          setEvents(response.data);
        } else {
          setEvents([]);
        }
      } catch (error) {
        if (error.name === "AbortError") return; 
        console.error("Failed to fetch calendar data:", error);
        setEvents([]);
      } finally {
        if (!controller.signal.aborted) setLoading(false); 
      }
    };

    fetchCalendarData();

    return () => controller.abort(); // 👈
  }, [authContext, date]);

  let filteredEvents = [];
  if (eventTypeFilter === "course") {
    filteredEvents = events.filter(
      (ev) =>
        ev.type?.toLowerCase() === "course" &&
        (courseFilter === "" || ev.id.toString() === courseFilter)
    );
  } else {
    filteredEvents = events.filter(
      (ev) => ev.type?.toLowerCase() === eventTypeFilter
    );
  }

  const courses = events.filter((ev) => ev.type?.toLowerCase() === "course");

  function renderTooltip(ev, type, currentDate) {
    if (type === "course") {
      const start = new Date(ev.plannedStartDate);
      const end = new Date(ev.plannedEndDate);
      const color = getCourseColor(ev.id);
      const isStart =
        currentDate && currentDate.toDateString() === start.toDateString();
      const isEnd =
        currentDate && currentDate.toDateString() === end.toDateString();

      return (
        <div
          key={`tooltip-course-${ev.id}-${isStart ? "start" : isEnd ? "end" : "mid"}`}
          style={{ fontSize: 11, minWidth: 160 }}
        >
          <div style={{ marginBottom: 4 }}>
            <strong
              style={{ color, textTransform: "capitalize", fontSize: 12 }}
            >
              {ev.name}
            </strong>
          </div>
          <div style={{ color: "#666" }}>
            {isStart ? "Start: " : isEnd ? "End: " : "In Progress "}
            {start.toLocaleDateString("en-GB")} &nbsp;
            <FiArrowRight color={color} /> &nbsp;
            {end.toLocaleDateString("en-GB")}
          </div>
        </div>
      );
    }

    if (type === "quiz" || type === "assessment") {
      return (
        <div key={`tooltip-${ev.id}`} style={{ fontSize: 11, minWidth: 120 }}>
          <div
            style={{
              fontWeight: 600,
              color: eventColors[type],
              textTransform: "capitalize",
              marginBottom: 2,
            }}
          >
            {ev.name}
          </div>
          <div style={{ color: "#666" }}>
            Scheduled date:{" "}
            {ev.scheduledDate
              ? new Date(ev.scheduledDate).toLocaleDateString("en-GB")
              : ""}
          </div>
        </div>
      );
    }

    return null;
  }

  function getDayEventHighlight(dateObj) {
    const iso = dateObj.toISOString().split("T")[0];

    const coursesOnDate = filteredEvents.filter(
      (ev) =>
        ev.type?.toLowerCase() === "course" &&
        dateObj >= new Date(ev.plannedStartDate) &&
        dateObj <= new Date(ev.plannedEndDate)
    );

    if (filteredEvents.length > 0 && coursesOnDate.length === 0) {
      for (const ev of filteredEvents) {
        const type = ev.type?.toLowerCase();
        if (
          (type === "assessment" || type === "quiz") &&
          ev.scheduledDate &&
          iso === new Date(ev.scheduledDate).toISOString().split("T")[0]
        ) {
          return {
            highlightType: type,
            bgColor: eventColors[type],
            tooltipData: [renderTooltip(ev, type, dateObj)],
          };
        }
      }
      return { highlightType: null, bgColor: null, tooltipData: [] };
    }

    if (coursesOnDate.length === 0) {
      return { highlightType: null, bgColor: null, tooltipData: [] };
    }

    const startCourses = [];
    const endCourses = [];
    const middleCourses = [];

    coursesOnDate.forEach((ev) => {
      const startDate = new Date(ev.plannedStartDate);
      const endDate = new Date(ev.plannedEndDate);

      if (dateObj.toDateString() === startDate.toDateString()) startCourses.push(ev);
      else if (dateObj.toDateString() === endDate.toDateString()) endCourses.push(ev);
      else middleCourses.push(ev);
    });

    const getColor = (ev) => getCourseColor(ev.id);

    let bgColor = "transparent";

    if (startCourses.length > 1) {
      const splitColors = startCourses.map(getColor);
      bgColor =
        "linear-gradient(90deg, " +
        splitColors
          .map(
            (color, idx) =>
              `${color} ${(100 / splitColors.length) * idx}%, ${color} ${(100 / splitColors.length) * (idx + 1)}%`
          )
          .join(", ") +
        ")";
    } else if (startCourses.length === 1) {
      bgColor = getColor(startCourses[0]);
    } else if (endCourses.length > 1) {
      const splitColors = endCourses.map(getColor);
      bgColor =
        "linear-gradient(90deg, " +
        splitColors
          .map(
            (color, idx) =>
              `${color} ${(100 / splitColors.length) * idx}%, ${color} ${(100 / splitColors.length) * (idx + 1)}%`
          )
          .join(", ") +
        ")";
    } else if (endCourses.length === 1) {
      bgColor = getColor(endCourses[0]);
    } else if (middleCourses.length === 1) {
      bgColor = getColor(middleCourses[0]) + "33";
    } else if (middleCourses.length > 1) {
      bgColor = "transparent";
    }

    const tooltipEvents = [];
    coursesOnDate.forEach((ev) => {
      tooltipEvents.push(renderTooltip(ev, "course", dateObj));
    });

    filteredEvents.forEach((ev) => {
      const type = ev.type?.toLowerCase();
      if (
        (type === "assessment" || type === "quiz") &&
        ev.scheduledDate &&
        iso === new Date(ev.scheduledDate).toISOString().split("T")[0]
      ) {
        tooltipEvents.push(renderTooltip(ev, type, dateObj));
      }
    });

    return { highlightType: "course", bgColor, tooltipData: tooltipEvents };
  }

  const cells = getDaysInMonth(date.getFullYear(), date.getMonth());

  return (
    <>
      <style>{tooltipStyles}</style>
      <Card
        className="p-3 charts"
        style={{
          height: "357px",
          borderRadius: 12,
          boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
          border: "1px solid #eee",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          gap: "8px",
        }}
      >
        <div className="d-flex justify-content-between flex-wrap mb-2">
          <div
            className="d-flex align-items-center gap-2 mb-2"
            style={{ fontSize: 12 }}
          >
            <div style={{ minWidth: 80 }}>
              {loading ? (
                <Skeleton width="100%" height="30px" />
              ) : (
                <select
                  value={eventTypeFilter}
                  onChange={(e) => {
                    setEventTypeFilter(e.target.value);
                    setCourseFilter("");
                  }}
                  style={{
                    fontSize: 12,
                    border: "1px solid #ddd",
                    borderRadius: 4,
                      maxWidth: "80px",
                    padding: "4px 8px",
                    backgroundColor: isDark ? "#222" : "#fff",
                    color: isDark ? "#eaeaea" : "#000",
                  }}
                >
                  <option value="course">Course</option>
                  <option value="assessment">Assessment</option>
                  <option value="quiz">Quiz</option>
                </select>
              )}
            </div>

            {eventTypeFilter === "course" && (
              <div style={{ minWidth: 100 }}>
                {loading ? (
                  <Skeleton width="100%" height="30px" />
                ) : (
                  <select
                    value={courseFilter}
                    onChange={(e) => setCourseFilter(e.target.value)}
                    style={{
                      maxWidth: "120px",
                      backgroundColor: isDark ? "#222" : "#fff",
                      color: isDark ? "#eaeaea" : "#000",
                      fontSize: 12,
                      border: "1px solid #ddd",
                      borderRadius: 4,
                      padding: "4px 8px",
                    }}
                  >
                    <option value="">All courses</option>
                    {courses.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            )}
          </div>

          <div className="d-flex">
            <button
              className="btn btn-light btn-sm py-0 px-2 me-1 me-lg-1"
              onClick={() => changeMonth(-1)}
              disabled={loading || isPrevDisabled}
              title={isPrevDisabled ? "Cannot go before Jan 2014" : ""}
            >
              &lt;
            </button>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 4,
          
                justifyContent: "center",
              }}
            >
              {loading ? (
                <Skeleton width="30px" height="30px" />
              ) : (
                <span style={{ fontWeight: 500 }}>
                  {monthNames[date.getMonth()]}
                </span>
              )}

              {loading ? (
                <Skeleton width="60px" height="30px" />
              ) : (
                <select
                  value={date.getFullYear()}
                  onChange={handleYearChange}
                  disabled={loading}
                  style={{
                    fontSize: 12,
                    padding: "4px 8px",
                    borderRadius: 4,
                    border: "1px solid #ddd",
                    backgroundColor: isDark ? "#222" : "#fff",
                    color: isDark ? "#eaeaea" : "#000",
                  }}
                >
                  {years.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              )}
            </div>

            <button
              className="btn btn-light btn-sm py-0 px-2 ms-1 ms-lg-2"
              onClick={() => changeMonth(1)}
              disabled={loading || isNextDisabled}
              title={isNextDisabled ? `Cannot go beyond Dec ${MAX_YEAR}` : ""}
            >
              &gt;
            </button>
          </div>
        </div>

        {loading ? (
          <div
            style={{
              flexGrow: 1,
              display: "grid",
              gridTemplateColumns: "repeat(7, 1fr)",
              gap: "6px",
            }}
          >
            {days.map((_, i) => (
              <Skeleton key={`day-skel-${i}`} width="100%" height="20px" />
            ))}
            {Array.from({ length: 35 }).map((_, i) => (
              <Skeleton key={`cell-skel-${i}`} width="100%" height="30px" />
            ))}
          </div>
        ) : (
          <div
            className="calendar-grid text-center"
            style={{
              flexGrow: 1,
              display: "grid",
              gridTemplateColumns: "repeat(7, 1fr)",
              gap: "6px",
              fontSize: 12,
              height: "100%",
            }}
          >
            {days.map((day) => (
              <div
                key={day}
                className="fw-bold"
                style={{ color: isDark ? "#eaeaea" : "#333", fontSize: 11 }}
              >
                {day}
              </div>
            ))}

            {cells.map((cell, idx) => {
              if (!cell) return <div key={idx} style={{ height: "32px" }}></div>;

              const fullDate = new Date(
                date.getFullYear(),
                date.getMonth(),
                cell
              );
              const { highlightType, bgColor, tooltipData } =
                getDayEventHighlight(fullDate);

              const coursesOnDate = filteredEvents.filter(
                (ev) =>
                  ev.type?.toLowerCase() === "course" &&
                  fullDate >= new Date(ev.plannedStartDate) &&
                  fullDate <= new Date(ev.plannedEndDate)
              );

              const textColor =
                highlightType && highlightType !== "course"
                  ? "#fff"
                  : isDark
                  ? "#eaeaea"
                  : "#000";

              return (
                <OverlayTrigger
                  key={idx}
                  placement="top"
                  overlay={
                    tooltipData.length > 0 ? (
                      <Tooltip id={`tooltip-${idx}`}>
                        {tooltipData}
                      </Tooltip>
                    ) : (
                      <Tooltip id={`tooltip-${idx}`} style={{ display: "none" }}>
                        <div></div>
                      </Tooltip>
                    )
                  }
                >
                  <div
                    className="calendar-cell rounded"
                    style={{
                      background: bgColor || "transparent",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 12,
                      height: 36,
                      color: textColor,
                      border: "none",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      padding: "2px 4px",
                      minHeight: 36,
                    }}
                  >
                    <div>{cell}</div>

                    <div
                      style={{
                        display: "flex",
                        gap: 2,
                        marginTop: 2,
                        justifyContent: "center",
                        flexWrap: "wrap",
                      }}
                    >
                      {coursesOnDate.map((course) => (
                        <span
                          key={`dot-${course.id}`}
                          style={{
                            width: 6,
                            height: 6,
                            borderRadius: "50%",
                            backgroundColor: getCourseColor(course.id),
                            border: "1px solid #fff",
                            boxShadow: "0 0 2px rgba(0,0,0,0.15)",
                          }}
                          title={course.name}
                        />
                      ))}
                    </div>
                  </div>
                </OverlayTrigger>
              );
            })}
          </div>
        )}
      </Card>
    </>
  );
};

export default CalenderComponent;